import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import ProLayout from '@/components/layouts/ProLayout';
import { 
  CheckCircle2, Circle, Mail, CreditCard, BookOpen, FileText,
  Search, Filter, RefreshCw, ChevronDown, ChevronUp, Send,
  User, Calendar, Clock, AlertCircle, CheckCheck, LayoutDashboard,
  Users, UserCog, Video, BarChart3, ClipboardList, Settings, BookMarked,
  Gamepad2, PenTool, Languages, Eye
} from 'lucide-react';

const EnrollmentsPage = () => {
  const { api } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  
  // Modal states
  const [showStartDateModal, setShowStartDateModal] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [sendNotification, setSendNotification] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Sidebar items (same as AdminDashboard)
  const sidebarItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/users', label: 'User Management', icon: Users },
    { path: '/admin/enrollments', label: 'Enrollments', icon: ClipboardList },
    { path: '/admin/allocate', label: 'Subject Allocation', icon: BookOpen },
    { path: '/admin/tutors', label: 'Tutor Assignment', icon: UserCog },
    { path: '/tutoring', label: 'Live Sessions', icon: Video },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/assessments', label: 'Assessments', icon: FileText },
    { path: '/admin/reports', label: 'Monthly Reports', icon: Calendar },
    { path: '/admin/settings', label: 'Settings', icon: Settings },
    { divider: true, label: 'Content Preview' },
    { path: '/admin/curriculum-browser', label: 'Curriculum Browser', icon: BookMarked },
  ];

  useEffect(() => {
    fetchEnrollments();
    fetchStats();
  }, []);

  const fetchEnrollments = async () => {
    try {
      const res = await api.get('/admin/enrollments');
      setEnrollments(res.data.enrollments || []);
    } catch (err) {
      console.error('Error fetching enrollments:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/enrollments/stats/summary');
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleResendEmail = async (enrollmentId) => {
    if (!confirm('Resend welcome email with login credentials?')) return;
    
    setProcessing(true);
    try {
      await api.post(`/admin/enrollments/${enrollmentId}/resend-welcome-email`);
      alert('Welcome email sent successfully!');
      fetchEnrollments();
    } catch (err) {
      alert('Failed to send email: ' + (err.response?.data?.detail || err.message));
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkLetterSent = async () => {
    if (!selectedEnrollment || !startDate) return;
    
    setProcessing(true);
    try {
      await api.post(`/admin/enrollments/${selectedEnrollment.id}/mark-letter-sent`, {
        start_date: startDate,
        send_notification: sendNotification
      });
      alert('Start date letter marked as sent!');
      setShowStartDateModal(false);
      setSelectedEnrollment(null);
      setStartDate('');
      fetchEnrollments();
    } catch (err) {
      alert('Failed to update: ' + (err.response?.data?.detail || err.message));
    } finally {
      setProcessing(false);
    }
  };

  const openStartDateModal = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setShowStartDateModal(true);
  };

  const filteredEnrollments = enrollments.filter(e => {
    const matchesSearch = 
      e.child_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.parent_email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filter === 'all') return matchesSearch;
    if (filter === 'pending_letter') return matchesSearch && !e.checklist?.start_date_letter_sent?.completed;
    if (filter === 'pending_email') return matchesSearch && !e.checklist?.login_email_sent?.completed;
    if (filter === 'complete') return matchesSearch && e.checklist_progress?.percentage === 100;
    return matchesSearch;
  });

  const ChecklistItem = ({ item, label, icon: Icon, onAction, actionLabel }) => {
    const isComplete = item?.completed;
    
    return (
      <div className={`flex items-center justify-between p-3 rounded-lg ${isComplete ? 'bg-green-50' : 'bg-gray-50'}`}>
        <div className="flex items-center gap-3">
          {isComplete ? (
            <CheckCircle2 className="w-5 h-5 text-green-600" />
          ) : (
            <Circle className="w-5 h-5 text-gray-400" />
          )}
          <Icon className={`w-4 h-4 ${isComplete ? 'text-green-600' : 'text-gray-500'}`} />
          <span className={isComplete ? 'text-green-800' : 'text-gray-700'}>{label}</span>
        </div>
        <div className="flex items-center gap-2">
          {isComplete && item?.completed_at && (
            <span className="text-xs text-gray-500">
              {new Date(item.completed_at).toLocaleDateString()}
            </span>
          )}
          {item?.auto === false && !isComplete && onAction && (
            <button
              onClick={onAction}
              className="px-3 py-1 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              {actionLabel || 'Mark Complete'}
            </button>
          )}
          {item?.auto === true && !isComplete && onAction && (
            <button
              onClick={onAction}
              className="px-3 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {actionLabel || 'Resend'}
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <ProLayout sidebarItems={sidebarItems} currentPath="/admin/enrollments">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-purple-600 animate-spin" />
        </div>
      </ProLayout>
    );
  }

  return (
    <ProLayout sidebarItems={sidebarItems} currentPath="/admin/enrollments">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Enrollment Management</h1>
          <p className="text-gray-600 mt-1">Track and manage student enrollments</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-5 shadow-sm border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.total_enrollments}</p>
                  <p className="text-sm text-gray-500">Total Enrollments</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-5 shadow-sm border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.pending_start_letter}</p>
                  <p className="text-sm text-gray-500">Pending Start Letter</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-5 shadow-sm border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCheck className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.fully_complete}</p>
                  <p className="text-sm text-gray-500">Fully Complete</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-5 shadow-sm border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">£{stats.total_revenue}</p>
                  <p className="text-sm text-gray-500">Total Revenue</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border mb-6">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            
            <div className="flex gap-2">
              {['all', 'pending_letter', 'pending_email', 'complete'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === f 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {f === 'all' ? 'All' : f === 'pending_letter' ? 'Pending Letter' : f === 'pending_email' ? 'Pending Email' : 'Complete'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Enrollments List */}
        <div className="space-y-4">
          {filteredEnrollments.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center border">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No enrollments found</p>
            </div>
          ) : (
            filteredEnrollments.map(enrollment => (
              <div key={enrollment.id} className="bg-white rounded-xl shadow-sm border overflow-hidden">
                {/* Header Row */}
                <div 
                  className="p-5 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedId(expandedId === enrollment.id ? null : enrollment.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {enrollment.child_name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{enrollment.child_name}</h3>
                        <p className="text-sm text-gray-500">{enrollment.parent_email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      {/* Subjects */}
                      <div className="hidden md:flex gap-1">
                        {enrollment.subjects?.map(s => (
                          <span key={s} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full capitalize">
                            {s}
                          </span>
                        ))}
                      </div>
                      
                      {/* Progress */}
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              enrollment.checklist_progress?.percentage === 100 
                                ? 'bg-green-500' 
                                : 'bg-purple-500'
                            }`}
                            style={{ width: `${enrollment.checklist_progress?.percentage || 0}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600">
                          {enrollment.checklist_progress?.completed}/{enrollment.checklist_progress?.total}
                        </span>
                      </div>
                      
                      {expandedId === enrollment.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Expanded Checklist */}
                {expandedId === enrollment.id && (
                  <div className="px-5 pb-5 border-t bg-gray-50">
                    <div className="pt-4 space-y-3">
                      <h4 className="font-semibold text-gray-700 mb-3">Enrollment Checklist</h4>
                      
                      <ChecklistItem 
                        item={enrollment.checklist?.subjects_selected}
                        label="Subjects Selected"
                        icon={BookOpen}
                      />
                      
                      <ChecklistItem 
                        item={enrollment.checklist?.payment_made}
                        label={`Payment Made ${enrollment.checklist?.payment_made?.amount ? `(£${enrollment.checklist.payment_made.amount})` : ''}`}
                        icon={CreditCard}
                      />
                      
                      <ChecklistItem 
                        item={enrollment.checklist?.login_email_sent}
                        label="Login Email Sent"
                        icon={Mail}
                        onAction={() => handleResendEmail(enrollment.id)}
                        actionLabel="Resend Email"
                      />
                      
                      <ChecklistItem 
                        item={enrollment.checklist?.start_date_letter_sent}
                        label={`Start Date Letter ${enrollment.checklist?.start_date_letter_sent?.start_date ? `(${enrollment.checklist.start_date_letter_sent.start_date})` : ''}`}
                        icon={FileText}
                        onAction={() => openStartDateModal(enrollment)}
                        actionLabel="Set Start Date"
                      />
                      
                      {/* Additional Info */}
                      <div className="mt-4 pt-4 border-t flex gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Enrolled: {new Date(enrollment.created_at).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          Year {enrollment.year_group} ({enrollment.key_stage?.toUpperCase()})
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Start Date Modal */}
        {showStartDateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Set Start Date</h3>
              <p className="text-gray-600 mb-4">
                Set the start date for <strong>{selectedEnrollment?.child_name}</strong> at the learning centre.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div className="mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendNotification}
                    onChange={(e) => setSendNotification(e.target.checked)}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Send email notification to parent</span>
                </label>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowStartDateModal(false);
                    setSelectedEnrollment(null);
                    setStartDate('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMarkLetterSent}
                  disabled={!startDate || processing}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {processing ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ProLayout>
  );
};

export default EnrollmentsPage;
